import { useEffect, useState } from "react";

/**
 * Lightweight client-side user preferences.
 *
 * Currently holds the `numerals` preference that controls whether the Arabic
 * view renders Arabic-Indic digits (١٢٣) or Western digits (123). The English
 * view always uses Latin digits regardless of this value.
 *
 * Persisted to localStorage under `sij_prefs`. A custom event lets number
 * formatters re-render live when the value changes (mirrors `lib/theme.ts`).
 * The backend will later persist this in `user_preferences.numerals`
 * (docs/Sijilaty-Backend-Updates.md §B10/B15).
 */

export type NumeralsPref = "auto" | "ar" | "en";

/** Notification tone pack — varies the waveform/decay of synthesised motifs. */
export type NotifPack = "chime" | "soft" | "marimba" | "classic";

export interface Prefs {
  numerals: NumeralsPref;
  /** Whether the desktop sidebar is collapsed to an icons-only rail. */
  sidebarCollapsed: boolean;
  /** Master on/off for synthesised notification motifs (SijSound engine). */
  notifSound: boolean;
  /** Notification motif volume, 0..1. */
  notifVolume: number;
  /** Selected notification tone pack. */
  notifPack: NotifPack;
}

export const PREFS_STORAGE_KEY = "sij_prefs";
export const PREFS_CHANGE_EVENT = "sij:prefs-change";

const DEFAULT_PREFS: Prefs = {
  numerals: "auto",
  sidebarCollapsed: false,
  notifSound: true,
  notifVolume: 0.6,
  notifPack: "chime",
};

const NUMERALS_VALUES: readonly NumeralsPref[] = ["auto", "ar", "en"];
const NOTIF_PACK_VALUES: readonly NotifPack[] = ["chime", "soft", "marimba", "classic"];

function isNumeralsPref(value: unknown): value is NumeralsPref {
  return typeof value === "string" && (NUMERALS_VALUES as readonly string[]).includes(value);
}

function isNotifPack(value: unknown): value is NotifPack {
  return typeof value === "string" && (NOTIF_PACK_VALUES as readonly string[]).includes(value);
}

function clampVolume(value: unknown): number {
  if (typeof value !== "number" || Number.isNaN(value)) return DEFAULT_PREFS.notifVolume;
  return Math.min(1, Math.max(0, value));
}

export function getPrefs(): Prefs {
  if (typeof localStorage === "undefined") return { ...DEFAULT_PREFS };
  try {
    const raw = localStorage.getItem(PREFS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    const parsed = JSON.parse(raw) as Partial<Prefs>;
    return {
      numerals: isNumeralsPref(parsed.numerals) ? parsed.numerals : DEFAULT_PREFS.numerals,
      sidebarCollapsed:
        typeof parsed.sidebarCollapsed === "boolean"
          ? parsed.sidebarCollapsed
          : DEFAULT_PREFS.sidebarCollapsed,
      notifSound:
        typeof parsed.notifSound === "boolean" ? parsed.notifSound : DEFAULT_PREFS.notifSound,
      notifVolume: clampVolume(parsed.notifVolume),
      notifPack: isNotifPack(parsed.notifPack) ? parsed.notifPack : DEFAULT_PREFS.notifPack,
    };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

export function getNumeralsPref(): NumeralsPref {
  return getPrefs().numerals;
}

export function setNumeralsPref(value: NumeralsPref): void {
  if (typeof localStorage === "undefined") return;
  const next: Prefs = { ...getPrefs(), numerals: value };
  localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify(next));
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(PREFS_CHANGE_EVENT, { detail: next }));
  }
}

/**
 * React hook returning the current numerals preference and a setter. Re-renders
 * on changes from this tab (custom event) or other tabs (storage event), so any
 * component rendering numbers updates live when the user flips the toggle.
 */
export function useNumeralsPref(): [NumeralsPref, (value: NumeralsPref) => void] {
  const [pref, setPref] = useState<NumeralsPref>(() => getNumeralsPref());

  useEffect(() => {
    const sync = () => setPref(getNumeralsPref());
    window.addEventListener(PREFS_CHANGE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(PREFS_CHANGE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  return [pref, setNumeralsPref];
}

export function getSidebarCollapsed(): boolean {
  return getPrefs().sidebarCollapsed;
}

export function setSidebarCollapsed(value: boolean): void {
  if (typeof localStorage === "undefined") return;
  const next: Prefs = { ...getPrefs(), sidebarCollapsed: value };
  localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify(next));
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(PREFS_CHANGE_EVENT, { detail: next }));
  }
}

/**
 * React hook returning whether the desktop sidebar is collapsed and a setter.
 * Re-renders on changes from this tab (custom event) or other tabs (storage
 * event), so the sidebar rail and the main-content offset stay in sync.
 */
export function useSidebarCollapsed(): [boolean, (value: boolean) => void] {
  const [pref, setPref] = useState<boolean>(() => getSidebarCollapsed());

  useEffect(() => {
    const sync = () => setPref(getSidebarCollapsed());
    window.addEventListener(PREFS_CHANGE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(PREFS_CHANGE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  return [pref, setSidebarCollapsed];
}

/* ------------------------------------------------------------------------- *
 * Notification sound preferences (SijSound engine — see lib/sijSound.ts).
 * These three keys live in the same `sij_prefs` blob and fire the same change
 * event, so the settings UI and the audio engine stay in sync live. The engine
 * reads them on every play; the settings UI uses the hook below.
 * ------------------------------------------------------------------------- */

function writePrefs(patch: Partial<Prefs>): void {
  if (typeof localStorage === "undefined") return;
  const next: Prefs = { ...getPrefs(), ...patch };
  localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify(next));
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(PREFS_CHANGE_EVENT, { detail: next }));
  }
}

export interface NotifSoundPrefs {
  enabled: boolean;
  volume: number;
  pack: NotifPack;
}

export function getNotifSoundPrefs(): NotifSoundPrefs {
  const p = getPrefs();
  return { enabled: p.notifSound, volume: p.notifVolume, pack: p.notifPack };
}

export function setNotifSoundEnabled(value: boolean): void {
  writePrefs({ notifSound: value });
}

export function setNotifVolume(value: number): void {
  writePrefs({ notifVolume: clampVolume(value) });
}

export function setNotifPack(value: NotifPack): void {
  writePrefs({ notifPack: value });
}

/**
 * React hook returning the current notification-sound preferences plus setters.
 * Re-renders on changes from this tab (custom event) or other tabs (storage
 * event), mirroring `useNumeralsPref`/`useSidebarCollapsed`.
 */
export function useNotifSoundPrefs(): {
  prefs: NotifSoundPrefs;
  setEnabled: (value: boolean) => void;
  setVolume: (value: number) => void;
  setPack: (value: NotifPack) => void;
} {
  const [prefs, setPrefs] = useState<NotifSoundPrefs>(() => getNotifSoundPrefs());

  useEffect(() => {
    const sync = () => setPrefs(getNotifSoundPrefs());
    window.addEventListener(PREFS_CHANGE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(PREFS_CHANGE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  return {
    prefs,
    setEnabled: setNotifSoundEnabled,
    setVolume: setNotifVolume,
    setPack: setNotifPack,
  };
}
